import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { templates as templatesApi } from "../lib/api";
import { useApp } from "../context/AppContext";
import { localizedTemplate } from "../lib/i18nContent";
import { toast } from "sonner";
import {
  Plus, ShoppingBag, Store as StoreIcon, Image as ImageIcon, Package,
  HeartHandshake, UserCog, Sparkles, Layers, Trash2, ArrowRight, ListChecks, Check,
} from "lucide-react";

const TEMPLATE_VISUALS = {
  Ventas: { icon: ShoppingBag, gradient: "from-amber-500 to-orange-600" },
  Tienda: { icon: StoreIcon, gradient: "from-sky-500 to-blue-600" },
  Imagen: { icon: ImageIcon, gradient: "from-fuchsia-500 to-pink-600" },
  Producto: { icon: Package, gradient: "from-emerald-500 to-teal-600" },
  Servicio: { icon: HeartHandshake, gradient: "from-rose-500 to-red-600" },
  Gerente: { icon: UserCog, gradient: "from-indigo-500 to-violet-600" },
  Limpieza: { icon: Sparkles, gradient: "from-cyan-500 to-sky-600" },
};

function visualFor(name) {
  return TEMPLATE_VISUALS[name] || { icon: Layers, gradient: "from-stone-500 to-stone-700" };
}

export default function StartAuditDialog({ open, onOpenChange, store, defaultAuditNumber, onConfirm }) {
  const { t, i18n } = useTranslation();
  const { user } = useApp();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    manager_name: "",
    address: "",
    audit_number: defaultAuditNumber || "",
    templates: [],
  });
  const [newOpen, setNewOpen] = useState(false);
  const [newTpl, setNewTpl] = useState({ name: "", description: "" });

  useEffect(() => {
    if (!open) return;
    const today = new Date();
    const yyyy = today.getFullYear();
    const auto = defaultAuditNumber || `AUD-${yyyy}-${String(Math.floor(Math.random() * 999) + 1).padStart(3, "0")}`;
    setForm((f) => ({
      ...f,
      address: store?.ciudad ? `${store.tienda} · ${store.ciudad}` : (store?.tienda || ""),
      audit_number: auto,
      templates: [],
    }));
    refreshTemplates();
  }, [open, store, defaultAuditNumber]);

  const refreshTemplates = async () => {
    try {
      const list = await templatesApi.list();
      setTemplates(list);
    } catch (e) {
      console.error(e);
    }
  };

  const today = useMemo(() => new Date().toLocaleDateString(), []);
  const canManageTemplates = user?.role === "admin";

  const toggleTemplate = (name) => {
    setForm((f) => {
      const set = new Set(f.templates || []);
      if (set.has(name)) set.delete(name);
      else set.add(name);
      return { ...f, templates: Array.from(set) };
    });
  };

  const submit = async () => {
    if (!form.manager_name.trim()) {
      toast.error(t("cover.manager_required"));
      return;
    }
    if (!form.templates || form.templates.length === 0) {
      toast.error(t("cover.template_required"));
      return;
    }
    setLoading(true);
    try {
      await onConfirm({
        manager_name: form.manager_name.trim(),
        address: form.address.trim(),
        audit_number: form.audit_number.trim(),
        template: form.templates[0],
        templates: form.templates,
      });
    } finally {
      setLoading(false);
    }
  };

  const createTemplate = async () => {
    if (!newTpl.name.trim()) return;
    try {
      const created = await templatesApi.create({ name: newTpl.name.trim(), description: newTpl.description });
      toast.success(t("cover.template_created"));
      setNewOpen(false);
      setNewTpl({ name: "", description: "" });
      await refreshTemplates();
      // Auto-select the new template alongside the others.
      setForm((f) => ({ ...f, templates: [...(f.templates || []), created.name] }));
    } catch (e) {
      const msg = e?.response?.data?.detail || t("cover.template_exists");
      toast.error(msg);
    }
  };

  const deleteTemplate = async (tpl) => {
    if (tpl.is_default) return;
    if (!window.confirm(t("cover.delete_template_confirm", { name: tpl.name }))) return;
    try {
      await templatesApi.remove(tpl.id);
      await refreshTemplates();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="start-audit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <ListChecks size={20} className="text-[#1E3A8A]" />
            {t("cover.title")}
          </DialogTitle>
          <p className="text-sm text-stone-500">{t("cover.subtitle")} <strong>{store?.tienda}</strong></p>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <div>
            <label className="canales-overline">{t("cover.auditor_name")}</label>
            <Input value={user?.name || ""} disabled className="mt-1 bg-stone-50" data-testid="cover-auditor-name" />
          </div>
          <div>
            <label className="canales-overline">{t("cover.date")}</label>
            <Input value={today} disabled className="mt-1 bg-stone-50" data-testid="cover-date" />
          </div>
          <div>
            <label className="canales-overline">{t("cover.manager_name")} *</label>
            <Input
              value={form.manager_name}
              onChange={(e) => setForm({ ...form, manager_name: e.target.value })}
              placeholder={t("cover.manager_placeholder")}
              className="mt-1"
              data-testid="cover-manager-name"
            />
          </div>
          <div>
            <label className="canales-overline">{t("cover.audit_number")}</label>
            <Input
              value={form.audit_number}
              onChange={(e) => setForm({ ...form, audit_number: e.target.value })}
              className="mt-1"
              data-testid="cover-audit-number"
            />
            <p className="text-[11px] text-stone-400 mt-1">{t("cover.audit_number_hint")}</p>
          </div>
          <div className="md:col-span-2">
            <label className="canales-overline">{t("cover.address")}</label>
            <Input
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              placeholder={t("cover.address_placeholder")}
              className="mt-1"
              data-testid="cover-address"
            />
          </div>
        </div>

        <div className="mt-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="font-display text-lg">{t("cover.choose_template_multi")}</h3>
              <p className="text-xs text-stone-500">{t("cover.template_subtitle")}</p>
            </div>
            {canManageTemplates && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setNewOpen(true)}
                className="gap-1.5 rounded-full"
                data-testid="new-template-btn"
              >
                <Plus size={14} /> {t("cover.new_template")}
              </Button>
            )}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-4">
            {templates.map((tpl) => {
              const v = visualFor(tpl.name);
              const Icon = v.icon;
              const active = (form.templates || []).includes(tpl.name);
              return (
                <Card
                  key={tpl.id}
                  onClick={() => toggleTemplate(tpl.name)}
                  className={`relative cursor-pointer transition-all overflow-hidden p-0 hover:shadow-md ${
                    active
                      ? "ring-2 ring-[#1E3A8A] shadow-md"
                      : "ring-1 ring-stone-200"
                  }`}
                  data-testid={`tpl-card-${tpl.name}`}
                >
                  <div className={`h-16 bg-gradient-to-br ${v.gradient} relative flex items-end p-3`}>
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.3),transparent_60%)]" />
                    <Icon size={18} className="relative text-white" />
                    {active && (
                      <span className="absolute top-2 right-2 w-6 h-6 rounded-full bg-white grid place-items-center shadow-md">
                        <Check size={13} className="text-[#1E3A8A]" />
                      </span>
                    )}
                  </div>
                  <div className="p-3">
                    <div className="text-sm font-semibold truncate">
                      {localizedTemplate(tpl.name, i18n.language)}
                    </div>
                    <div className="text-[11px] text-stone-500 mt-0.5">
                      {tpl.question_count > 0
                        ? t("cover.questions_count", { n: tpl.question_count })
                        : t("cover.no_questions")}
                    </div>
                  </div>
                  {canManageTemplates && !tpl.is_default && (
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteTemplate(tpl); }}
                      className="absolute top-1 right-1 p-1 rounded-full bg-white/80 hover:bg-white text-stone-500 hover:text-red-600"
                    >
                      <Trash2 size={11} />
                    </button>
                  )}
                </Card>
              );
            })}
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("common.cancel")}</Button>
          <Button
            className="brand-button gap-2"
            onClick={submit}
            disabled={loading || (form.templates || []).length === 0}
            data-testid="cover-start-button"
          >
            {(form.templates || []).length === 1
              ? t("cover.start_audit_with", { template: localizedTemplate(form.templates[0], i18n.language) })
              : (form.templates || []).length > 1
              ? t("cover.start_audit_multi", { n: form.templates.length })
              : t("cover.start")}
            <ArrowRight size={14} />
          </Button>
        </DialogFooter>

        {/* Nested dialog: create new template */}
        <Dialog open={newOpen} onOpenChange={setNewOpen}>
          <DialogContent className="max-w-md" data-testid="new-template-dialog">
            <DialogHeader>
              <DialogTitle>{t("cover.new_template_dialog")}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="canales-overline">{t("cover.template_name")}</label>
                <Input
                  value={newTpl.name}
                  onChange={(e) => setNewTpl({ ...newTpl, name: e.target.value })}
                  className="mt-1"
                  data-testid="new-template-name"
                  autoFocus
                />
              </div>
              <div>
                <label className="canales-overline">{t("cover.template_description")}</label>
                <Input
                  value={newTpl.description}
                  onChange={(e) => setNewTpl({ ...newTpl, description: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setNewOpen(false)}>{t("common.cancel")}</Button>
              <Button onClick={createTemplate} className="brand-button" data-testid="new-template-save">
                {t("common.save")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

export { TEMPLATE_VISUALS, visualFor };
